// An access point's VLANs against the switch port it hangs off, on its own
// page (#1448, ADR-0269 decision 6, `6d`).
//
// **One producer, read here and judged nowhere here.** The rows are the site
// comparison's `reach` facet (`GET /sites/{id}/comparison`) filtered to this
// access point; the comparison, the Topology's Findings and this page read the
// same rows, so they cannot disagree. The comparison is a whole site's read, so
// this page asks for it only when the operator does, as the Topology does
// (#907) — an absent answer is said as not asked, never as nothing wrong.
//
// **The two caveats are said once, above the rows** (#1461), and decision 4d
// with them where this access point's untagged upstream is shown (#1482): each row's own
// sentence is shown without them (`../reachCaveats.tsx`), an `agree` row is
// one line that opens to that sentence, and a `differs` or `unanswered` row is
// always shown whole.
//
// **A switch's page shows its own uplink row the same way** (ADR-0310 `7a`;
// #1585): the one `{serial}/uplink` row, read from the same facet.
import { type ReactNode, useState } from "react";
import { api } from "../../api/client";
import { SKIP, useQuery } from "../../api/hooks";
import type { components } from "../../api/schema";
import { Card, Info, Pill, ProblemBox } from "../../ui";
import { dispositionOf } from "../comparisonWords";
import { isUplink, ReachCaveats, reachDetail, reachNeed } from "../reachCaveats";
import { ClosedIcon, Glyph, OpenIcon } from "../../icons";

type Row = components["schemas"]["ComparisonRow"];

/** This access point's rows of the reach facet. A switch's uplink row is the
 * switch's and not any access point's, so it is never among them. */
export function reachRowsOf(rows: Row[], serial: string): Row[] {
  return rows.filter((r) => r.facet === "reach" && !isUplink(r) && (r.refs.serial === serial || r.element === serial || r.element.startsWith(serial + "/")));
}

/** This switch's own uplink row of the reach facet (ADR-0310 `7a`). */
export function uplinkRowsOf(rows: Row[], serial: string): Row[] {
  return rows.filter((r) => isUplink(r) && r.element === `${serial}/uplink`);
}

export default function AccessPointReach({ siteId, serial }: { siteId: string; serial: string }) {
  return (
    <ReachPanel siteId={siteId} label="Its switch port" select={(rows) => reachRowsOf(rows, serial)} none="The comparison holds no row for this access point.">
      {/* Restates ADR-0269 decision 1c and ADR-0305 decision 1 — both sides are the documents the
          cell holds, and an mpsk network's PAN VLANs are read from its PAN rows each time. Decision
          2a's "not what the port runs" and the blind spots are ReachCaveats, under it. */}
      Compares every VLAN this access point&rsquo;s configuration tags upstream, and its untagged upstream, with the switch
      port it connects to, as the configurations the controller holds state them. For each mpsk network it serves, the VLANs
      of the network&rsquo;s PANs are checked too, as one row.
    </ReachPanel>
  );
}

/** A switch's own uplink against what the access points on it need. */
export function SwitchUplinkReach({ siteId, serial }: { siteId: string; serial: string }) {
  return (
    <ReachPanel siteId={siteId} label="Its uplink" select={(rows) => uplinkRowsOf(rows, serial)}
      none="The comparison holds no uplink row for this switch. It makes one only for a switch an access point is placed on.">
      {/* Restates ADR-0310 decisions 2, 3 and 4 — the uplink the switch reports, an aggregate as every
          member, against the union of what the access points on the switch need, as the configuration
          the controller holds states it; one hop, so nothing upstream of the switch is read. */}
      Compares every VLAN the access points on this switch need with the uplink the switch reports, every member of it
      where it is an aggregate, as the configuration the controller holds states it. Nothing upstream of this switch is read.{" "}
      {/* Restates ADR-0556 decisions 1b, 2a and 3a — a VLAN the uplink does not carry that another trunk of the same
          configuration does is still missing, and the row names that trunk and says where the VLAN goes beyond it is not read. */}
      A VLAN missing on the uplink that another port of the configuration carries is still missing: the row names that port,
      and where the VLAN goes beyond it is not read.
    </ReachPanel>
  );
}

function ReachPanel({ siteId, label, select, none, children }: {
  siteId: string; label: string; select: (rows: Row[]) => Row[]; none: string; children: ReactNode;
}) {
  const [asked, setAsked] = useState(false);
  const q = useQuery(() => asked ? api.GET("/sites/{id}/comparison", { params: { path: { id: siteId } } }) : SKIP, [siteId, asked]);
  const rows = q.data ? select(q.data.rows) : [];
  return (
    // What the comparison reads, behind the ⓘ beside the title (#1900).
    <Card className="panel" title={label} head={<Info label="About this comparison">{children}</Info>}>
      <ReachCaveats rows={rows} />
      {!asked && <button type="button" className="btn btn-sm" onClick={() => setAsked(true)}>Read the site’s comparison</button>}
      {q.status === "loading" && asked && <p className="muted small">Loading the site’s comparison…</p>}
      {q.status === "error" && <ProblemBox problem={q.problem} />}
      {q.data && rows.length === 0 && <p className="muted small">{none}</p>}
      {rows.length > 0 && (
        <ul className="warnings">
          {rows.map((r) => r.disposition === "agree"
            ? <AgreeLine key={r.element} row={r} />
            : (
              <li key={r.element} data-reach={r.element} data-disposition={r.disposition}>
                <Pill tone={dispositionOf(r.disposition).tone}>{dispositionOf(r.disposition).label}</Pill>{" "}
                <span className="small">{reachDetail(r.detail) ?? r.reason?.detail}</span>
              </li>
            ))}
        </ul>
      )}
    </Card>
  );
}

/** An `agree` row: one line — its need, the switch and port, the verdict and
 * how the port was found — that opens to the row's own sentence. */
function AgreeLine({ row }: { row: Row }) {
  const [open, setOpen] = useState(false);
  const held = row.held?.fields ?? {};
  const need = reachNeed(row);
  return (
    <li data-reach={row.element} data-disposition={row.disposition} data-reach-line={open ? undefined : ""}>
      <button type="button" className="btn btn-quiet btn-sm" aria-expanded={open} onClick={() => setOpen(!open)}
        aria-label={`${open ? "Fold" : "Open"} ${need ?? row.element}`}>
        <Glyph icon={open ? OpenIcon : ClosedIcon} />
      </button>{" "}
      <Pill tone={dispositionOf(row.disposition).tone}>{dispositionOf(row.disposition).label}</Pill>{" "}
      <span className="small">
        {[need, held.switch ?? row.refs.switch_serial, row.refs.port].filter(Boolean).join(" · ")}
        {held.management_vlan && <> · declared management VLAN {held.management_vlan}</>}
        {held.join && <span className="muted"> · {held.join}</span>}
      </span>
      {open && <div className="small">{reachDetail(row.detail)}</div>}
    </li>
  );
}
